import React, { useCallback } from 'react';
import { Tooltip, Stack } from '@mui/material';
import DataGrid from 'ui/components/DataGrid';
import { tableSchemaBase as columns } from './Experts.config';
import useViewModal from 'hooks/shared/useViewModal';
import CoachViewModalWithRequest from 'ui/components/shared/coaches/CoachViewModalWithRequest';
import dayjs from 'dayjs';
import useFormSettings from 'hooks/usecase/useFormSettings';
import { useMeQuery } from 'hooks/queries';
import { useTranslation } from 'react-i18next';
import { getStringFromList } from 'utils/languages';

const Experts = ({
  activeBorder,
  experts,
  expertIdSelected,
  onClickSelect,
  setQueryParams,
  queryParams,
  isLoading,
  coachee,
  onCoachDetail,
  hiddenAddButton = false,
  hiddenColumns = [],
  showCoachViewInModal = true,
  defaultOrderBy,
  defaultOrderDirection = 'asc',
}) => {
  // Hooks
  const { state, actions } = useViewModal();
  const [orderByOrigin, setOrderByOrigin] = React.useState(defaultOrderBy || null);
  const { data: user } = useMeQuery();
  const qParams = { isMentor: true };
  const { formSettingsList } = useFormSettings(user.company.id, qParams);
  const { t } = useTranslation();
  const expertLabel = user.company.expertLabel;
  const sessionLabel = user.company.sessionLabel;

  console.log('defaultOrderBy Experts', defaultOrderBy);
  console.log('defaultOrderDirection Experts', defaultOrderDirection);

  // Apply default sort mapping on component mount
  React.useEffect(() => {
    if (defaultOrderBy && !queryParams.orderBy) {
      let mappedOrderBy = defaultOrderBy;
      switch (defaultOrderBy) {
        case 'scopeName':
          mappedOrderBy = 'userScope';
          break;
        case 'user':
          mappedOrderBy = 'firstname';
          break;
        case 'registrationDate':
          mappedOrderBy = 'createdAt';
          break;
        case 'max_capacity':
          mappedOrderBy = 'maxCapacityComputed';
          break;
        case 'languages_in_common':
          mappedOrderBy = 'languagesInCommon';
          break;
        case 'targets_in_common':
          mappedOrderBy = 'targetsInCommon';
          break;
        case 'matching_score':
          mappedOrderBy = 'matchingScore';
          break;
      }

      setQueryParams((prev) => ({
        ...prev,
        orderBy: mappedOrderBy,
        orderDirection: defaultOrderDirection,
      }));
    }
  }, [defaultOrderBy, defaultOrderDirection, setQueryParams]);

  const dataGridStyles = activeBorder
    ? { border: '1px solid rgb(224, 224, 224)', padding: '0 8px', borderRadius: '0', cursor: 'pointer' }
    : { cursor: 'pointer' };

  let positions = [
    { index: 1, title: 'Intern' },
    { index: 2, title: 'Individual' },
    { index: 3, title: 'Contributor' },
    { index: 4, title: 'Manager' },
    { index: 5, title: 'Senior Manager' },
    { index: 6, title: 'Director' },
    { index: 7, title: 'VP' },
    { index: 8, title: 'Executive' },
  ];
  function findIndexByTitle(title, positions) {
    // Recherche du titre dans le tableau positions
    for (var i = 0; i < positions.length; i++) {
      if (positions[i].title == title) {
        return parseInt(positions[i].index);
      }
    }

    return false;
  }

  const updateList = (users) =>
    users.reduce((acc, currentItem) => {
      let seniorityGap =
        Number.isInteger(
          findIndexByTitle(currentItem && currentItem.seniorityLevel ? currentItem.seniorityLevel : '', positions)
        ) &&
        Number.isInteger(findIndexByTitle(coachee && coachee.seniorityLevel ? coachee.seniorityLevel : '', positions))
          ? findIndexByTitle(currentItem && currentItem.seniorityLevel ? currentItem.seniorityLevel : '', positions) -
            findIndexByTitle(coachee && coachee.seniorityLevel ? coachee.seniorityLevel : '', positions)
          : '-';

      const addButtonDisabled =
        (currentItem.maxCapacityComputed <= 0 && currentItem.canExceedMaxCapacity === false) ||
        currentItem.rhValidation;

      const addButtonTooltip = addButtonDisabled
        ? currentItem.rhValidation
          ? t('cannot_be_selected')
          : t('coach_not_selectable', { expertLabel, sessionLabel })
        : '';

      const modifiedItem = {
        ...currentItem,
        name: currentItem.firstname + ' ' + currentItem.lastname,
        user: {
          name: currentItem.firstname + ' ' + currentItem.lastname,
          title: currentItem.title,
          picture: currentItem.picture,
        },
        scopeName: currentItem.userScope != null ? currentItem.userScope.name : '',
        registrationDate: dayjs(currentItem.registrationDate).fromNow(),
        languagesSpoken: currentItem.languagesSpoken ? currentItem.languagesSpoken.join(', ') : '',
        hasSubscriptions: currentItem.hasSubscriptions ? 'YES' : 'NO',
        internalCoach: currentItem.internalCoach ? 'INTERNAL' : 'EXTERNAL',
        //TODO : get equation to calcul availableSpots
        max_capacity: currentItem.maxCapacityComputed,
        expertLabel: user.company.expertLabel,
        sessionLabel: user.company.sessionLabel,
        addButtonDisabled,
        addButtonTooltip,
        languages_in_common: currentItem.languagesInCommon || 0,
        seniority_gap: seniorityGap,
        targets_in_common: currentItem.targetsInCommon || 0,
        matching_score: currentItem.matchingScore || '-',
        languages: currentItem.languagesSpoken ? getStringFromList(currentItem.languagesSpoken) : '',
      };
      acc.push(modifiedItem);
      return acc;
    }, []);

  const handleOnFilter = (filters, currentPage, currentSize) => {
    let queryParamsFilter = {};
    if (filters.items) {
      filters.items.map((filter) => {
        switch (filter.field) {
          case 'scopeName':
            queryParamsFilter = { ...queryParamsFilter, userScope: filter.value };
            break;
          case 'user':
            queryParamsFilter = { ...queryParamsFilter, name: filter.value };
            break;
          case 'registrationDate':
            queryParamsFilter = {
              ...queryParamsFilter,
              createdAt: filter.value && dayjs(filter.value).format('YYYY-MM-DD'),
            };
            break;
          default:
            queryParamsFilter = { ...queryParamsFilter, [filter.field]: filter.value };
            break;
        }
      });
    }

    setQueryParams({
      ...{ orderBy: queryParams.orderBy, orderDirection: queryParams.orderDirection },
      page: currentPage,
      size: currentSize,
      ...queryParamsFilter,
    });
  };

  const handleOnSort = (sort, currentPage, currentSize) => {
    let queryParamsSort = {};
    setOrderByOrigin(sort.orderBy);
    switch (sort.orderBy) {
      case 'scopeName':
        queryParamsSort = { ...sort, orderBy: 'userScope' };
        break;
      case 'user':
        queryParamsSort = { ...sort, orderBy: 'firstname' };
        break;
      case 'registrationDate':
        queryParamsSort = { ...sort, orderBy: 'createdAt' };
        break;
      case 'max_capacity':
        queryParamsSort = { ...sort, orderBy: 'maxCapacityComputed' };
        break;
      case 'languages_in_common':
        queryParamsSort = { ...sort, orderBy: 'languagesInCommon' };
        break;
      case 'targets_in_common':
        queryParamsSort = { ...sort, orderBy: 'targetsInCommon' };
        break;
      case 'matching_score':
        queryParamsSort = { ...sort, orderBy: 'matchingScore' };
        break;
      default:
        queryParamsSort = { ...sort };
        break;
    }
    setQueryParams({ ...queryParams, page: currentPage, size: currentSize, ...queryParamsSort });
  };

  function checkAllNulls(array, attribute) {
    return array.every(function (object) {
      return object[attribute] === null;
    });
  }
  function checkAllEmpty(array, attribute) {
    return array.every(function (object) {
      return object[attribute] === '-';
    });
  }

  const filterColumnFromSettings = (list) => {
    const EXCLUDE_LIST = [];
    formSettingsList?.formFields?.map((item) => {
      switch (item.fieldName) {
        case 'spokenLanguages':
          if (!item.isActivated) EXCLUDE_LIST.push('languages_in_common');
          break;
        case 'objectives_expertises':
          if (!item.isActivated) EXCLUDE_LIST.push('targets_in_common');
          break;
        case 'genderIdentity':
          if (!item.isActivated) EXCLUDE_LIST.push('gender');
          break;
      }
    });
    return list.filter((item) => !EXCLUDE_LIST.includes(item.field));
  };

  const getColumns = () => {
    let filteredColumns = JSON.parse(JSON.stringify(columns));

    if (hiddenAddButton) {
      const actionColumn = filteredColumns.find((column) => column.field === 'actions');
      actionColumn.hiddenAddButton = true;
      actionColumn.minWidth = 140;
    }

    if (
      experts &&
      experts.rows &&
      Array.isArray(updateList(experts.rows)) &&
      checkAllNulls(updateList(experts.rows), 'gender')
    ) {
      filteredColumns = columns.filter((col) => col.field !== 'gender');
    }
    if (
      experts &&
      experts.rows &&
      Array.isArray(updateList(experts.rows)) &&
      checkAllEmpty(updateList(experts.rows), 'seniority_gap')
    ) {
      filteredColumns = filteredColumns.filter((col) => col.field !== 'seniority_gap');
    }

    if (!user.company.matchingScoreActivated) {
      filteredColumns = filteredColumns.filter((col) => col.field !== 'matching_score');
    }

    filteredColumns = filteredColumns.filter((col) => !hiddenColumns.includes(col.field));

    return filterColumnFromSettings(filteredColumns);
  };

  // We only import this function when we need it because it calls a params property that computes a getRow on the mui side, which can sometimes create errors when changing pages.

  const getRowClassName = expertIdSelected
    ? (params) => {
        if (expertIdSelected === params.id) return 'Mui-selected';
        return '';
      }
    : undefined;

  return (
    <Stack sx={{ padding: '0px 20px', height: '520px' }}>
      <DataGrid
        key={`${experts?.currentPage}-${experts?.totalItems}-${queryParams.orderBy}-${queryParams.orderDirection}`}
        sx={dataGridStyles}
        getRowClassName={getRowClassName}
        sortModel={orderByOrigin ? [{ field: orderByOrigin, sort: queryParams.orderDirection?.toLowerCase() }] : []}
        currentPage={experts?.currentPage || 1}
        onChangeSize={() => {}}
        onChangePage={() => {}}
        progressPending={false}
        onRowClicked={(coach, event) => {
          if (coach?.row?.addButtonDisabled) return;
          if (!event.defaultMuiPrevented) {
            onClickSelect(coach.row);
          }
        }}
        onAdd={(expert) => {
          onClickSelect(expert);
        }}
        sizePages={[
          { id: 1, value: 5 },
          { id: 2, value: 10 },
          { id: 3, value: 15 },
        ]}
        onShow={(expert) => {
          actions.openModal(expert.id);
          onCoachDetail && onCoachDetail(expert.id);
        }}
        totalPages={experts && experts.totalPages}
        totalItems={experts && experts.totalItems}
        onPaginationChange={(page, nbrPages) => {
          setQueryParams({ ...queryParams, page: page, size: nbrPages });
        }}
        onFilter={handleOnFilter}
        onSort={handleOnSort}
        rows={experts !== undefined && updateList(experts.rows)}
        columns={getColumns()}
        customHeight="550px"
        loading={isLoading}
      />
      {showCoachViewInModal && (
        <CoachViewModalWithRequest
          userRole={user.role}
          coachId={state.viewId}
          open={state.open}
          onClose={actions.closeModal}
        />
      )}
    </Stack>
  );
};

export default Experts;
